import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "../experience/api";
import { DISH_PHOTOS, normName } from "../table/dishPhotos";
import { addToSharedCart } from "./LandingV2";
import "./landing.css";

const ROUND_SECS = 50;

export default function CatchTheNaan() {
  const navigate = useNavigate();
  const areaRef = useRef(null);
  const basketRef = useRef(null);
  const state = useRef({ naans: [], bx: 0.5, nextId: 1, lastSpawn: 0, start: 0, raf: 0 });
  const audioRef = useRef(null);
  const [phase, setPhase] = useState("ready"); // ready | playing | over
  const [score, setScore] = useState(0);
  const [combo, setCombo] = useState(0);
  const [muted, setMuted] = useState(true);
  const [pops, setPops] = useState([]);
  const [shabash, setShabash] = useState(0);
  const [timeLeft, setTimeLeft] = useState(ROUND_SECS);
  const [, setTick] = useState(0);
  const [naanDish, setNaanDish] = useState(null);

  useEffect(() => {
    api.get("/table-menu").then((r) => {
      const ds = (r.data.dishes || []).map((d) => ({ ...d, img: d.image || DISH_PHOTOS[normName(d.name)] || null }));
      setNaanDish(ds.find((d) => d.name.trim().toLowerCase() === "garlic naan") || ds.find((d) => d.name.toLowerCase().includes("garlic naan")) || ds.find((d) => d.name.toLowerCase().includes("naan")) || null);
    }).catch(() => {});
    return () => cancelAnimationFrame(state.current.raf); // eslint-disable-line react-hooks/exhaustive-deps
  }, []);

  const blip = (good) => {
    if (muted) return;
    try {
      if (!audioRef.current) audioRef.current = new (window.AudioContext || window.webkitAudioContext)();
      const ctx = audioRef.current;
      const o = ctx.createOscillator(); const g = ctx.createGain();
      o.frequency.value = good ? 660 : 160; g.gain.value = 0.06;
      o.connect(g); g.connect(ctx.destination);
      o.start(); o.stop(ctx.currentTime + 0.12);
    } catch { /* no audio */ }
  };

  const start = () => {
    state.current = { naans: [], bx: 0.5, nextId: 1, lastSpawn: 0, start: performance.now(), raf: 0 };
    setScore(0); setCombo(0); setTimeLeft(ROUND_SECS); setPhase("playing");
    loop();
  };

  const loop = () => {
    const s = state.current;
    const area = areaRef.current;
    if (!area) return;
    const H = area.clientHeight;
    const now = performance.now();
    const elapsed = (now - s.start) / 1000;
    const left = Math.max(0, ROUND_SECS - elapsed);
    setTimeLeft(Math.ceil(left));
    if (left <= 0) { setPhase("over"); return; }

    const gap = Math.max(520, 1000 - elapsed * 9);
    if (now - s.lastSpawn > gap) {
      s.lastSpawn = now;
      s.naans.push({ id: s.nextId++, x: 0.1 + Math.random() * 0.8, y: 90, vy: 2.1 + elapsed * 0.045 + Math.random() * 0.8, burnt: Math.random() < 0.24, rot: Math.random() * 60 - 30 });
    }
    const catchY = H - 96;
    const W = area.clientWidth;
    s.naans = s.naans.filter((n) => {
      n.y += n.vy;
      if (n.y > catchY && n.y < catchY + 46 && Math.abs(n.x * W - s.bx * W) < 82) {
        if (n.burnt) {
          blip(false);
          setScore((v) => Math.max(0, v - 1)); setCombo(0);
          setPops((p) => [...p.slice(-4), { id: n.id, x: n.x, y: n.y, txt: "-1", bad: true }]);
        } else {
          blip(true);
          setScore((v) => v + 1);
          setCombo((c) => {
            const nc = c + 1;
            if (nc > 0 && nc % 5 === 0) setShabash(Date.now());
            return nc;
          });
          setPops((p) => [...p.slice(-4), { id: n.id, x: n.x, y: n.y, txt: "+1" }]);
        }
        return false;
      }
      return n.y < H + 60;
    });
    setTick((t) => t + 1);
    s.raf = requestAnimationFrame(loop);
  };

  const onMove = (e) => {
    const area = areaRef.current;
    if (!area) return;
    const r = area.getBoundingClientRect();
    const cx = (e.touches ? e.touches[0].clientX : e.clientX) - r.left;
    state.current.bx = Math.min(0.95, Math.max(0.05, cx / r.width));
    if (basketRef.current) basketRef.current.style.left = `calc(${state.current.bx * 100}% - 75px)`;
  };

  const addNaan = () => {
    if (!naanDish) { navigate("/go/menu"); return; }
    addToSharedCart(naanDish);
    toast.success(`${naanDish.name} added`, { style: { background: "#221408", color: "#F4A300", border: "1px solid #D4AF37" } });
    navigate("/go/cart");
  };

  const s = state.current;
  return (
    <div className="naan-root">
      <div className="naan-top">
        <h1>Catch the Naan</h1>
        <span className="sc" data-testid="naan-score">Score {score}{combo >= 3 ? ` · x${combo} Tadka combo` : ""}</span>
        <span className="sc" style={{ marginLeft: "auto" }} data-testid="naan-timer">{phase === "playing" ? `${timeLeft}s` : ""}</span>
      </div>
      <p className="naan-fun">JUST FOR FUN · NO PRIZES</p>
      <div className="naan-area" ref={areaRef} data-testid="naan-game-area"
        onPointerMove={onMove} onTouchMove={onMove}>
        <button className="naan-mute" data-testid="naan-mute" onClick={() => setMuted((m) => !m)}>{muted ? "Sound off" : "Sound on"}</button>
        <img className="naan-tandoor" src="/assets/tandoor.png" alt="" />
        {phase === "playing" && s.naans.map((n) => (
          <img key={n.id} className={`naan-fly${n.burnt ? " burnt" : ""}`} src="/assets/naan.png" alt=""
            style={{ left: `calc(${n.x * 100}% - 37px)`, top: n.y, transform: `rotate(${n.rot}deg)` }} />
        ))}
        {pops.map((p) => (
          <span key={p.id} className="naan-pop" style={{ left: `${p.x * 100}%`, top: p.y, color: p.bad ? "#e05a3a" : "#F4A300" }}>{p.txt}!</span>
        ))}
        {shabash > 0 && Date.now() - shabash < 1400 && <span className="naan-shabash">Shabash! Oye hoye!</span>}
        <img className="naan-babaji" src="/assets/babaji-thumbs.png" alt="" />
        <div className="naan-basket" ref={basketRef} data-testid="naan-basket" style={{ left: "calc(50% - 75px)", bottom: 26 }} />

        {phase === "ready" && (
          <div className="naan-panel" data-testid="naan-start-panel">
            <h2 className="v2-serif">Naans fly out of the tandoor</h2>
            <p>Drag the basket to catch the fresh ones. Dodge the burnt ones. {ROUND_SECS} seconds, just for fun.</p>
            <div className="row">
              <button className="naan-btn fill" data-testid="naan-start" onClick={start}>Start</button>
              <button className="naan-btn line" data-testid="naan-home" onClick={() => navigate("/")}>Back home</button>
            </div>
          </div>
        )}
        {phase === "over" && (
          <div className="naan-panel" data-testid="naan-end-panel">
            <h2 className="v2-serif" data-testid="naan-final-score">Round over: {score} naan{score === 1 ? "" : "s"}!</h2>
            <p>Hungry now? The real one is fresh from our tandoor.</p>
            <div className="row">
              <button className="naan-btn fill" data-testid="naan-add-dish" onClick={addNaan}>
                Add {naanDish ? naanDish.name : "Garlic Naan"}{naanDish ? ` · $${Number(naanDish.price).toFixed(2)}` : ""}
              </button>
              <button className="naan-btn line" data-testid="naan-play-again" onClick={start}>Play again</button>
              <button className="naan-btn line" data-testid="naan-back-home" onClick={() => navigate("/")}>Home</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
